import React, { useState } from 'react';
import { ApiError, apiFetch } from '../../lib/api';
import type { TournamentSummary } from '../../hooks/useScoringTournament';

interface PublishCardProps {
  tournament: TournamentSummary;
  onSaved: (tournament: TournamentSummary) => void;
}

/** Publish / Unpublish: published tournaments are listed on /scoreboard and the home page's Live button. */
export function PublishCard({ tournament, onSaved }: PublishCardProps) {
  const published = tournament.published === true;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function toggle() {
    setBusy(true);
    setError(null);
    try {
      const body = await apiFetch<{ tournament: TournamentSummary }>(
        `/api/admin/shooting/tournaments/${tournament.id}/publish`,
        { method: 'PATCH', body: JSON.stringify({ published: !published }) },
      );
      onSaved(body.tournament);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-[var(--radius-lg)] border border-[var(--color-border)] p-4" aria-label="Publish">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-base font-semibold text-[var(--color-text-primary)]">
            <span
              aria-hidden
              className={`inline-block h-2.5 w-2.5 rounded-full ${published ? 'animate-pulse bg-[var(--color-error)]' : 'bg-[var(--color-border-strong)]'}`}
            />
            {published ? 'Published' : 'Not published'}
          </h2>
          <p className="mt-0.5 text-sm text-[var(--color-text-secondary)]">
            {published
              ? 'Shown on smymchennai.in as a Live button and listed on /scoreboard.'
              : 'Hidden from smymchennai.in and the /scoreboard list. Its direct link still works.'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void toggle()}
          disabled={busy}
          aria-pressed={published}
          className={`min-h-11 shrink-0 rounded-[var(--radius-md)] px-4 text-sm font-medium disabled:opacity-50 ${
            published
              ? 'bg-[var(--color-surface-2)] text-[var(--color-text-primary)]'
              : 'bg-[var(--color-accent)] text-white'
          }`}
        >
          {busy ? 'Saving…' : published ? 'Unpublish' : 'Publish'}
        </button>
      </div>
      {error ? (
        <p className="mt-2 text-sm font-medium text-[var(--color-error-text)]" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}
